// Invite links on the device (docs/design/social-plan.md, "Inviting"). A token travels in the URL
// fragment, which no server or referrer sees, then moves into this tab's sessionStorage before the
// app or analytics read the address bar, so it survives the sign-in round trip and nothing else.
import { z } from "zod";
import { InviteSecret } from "./invite";

const PENDING_KEY = "proef.pendingInvite";
const TOKENS_KEY = "proef.inviteTokens";
/** The account the remembered tokens belong to. */
const TOKENS_OWNER_KEY = "proef.inviteTokensOwner";
/** Links re-shareable from this device; older ones simply get a fresh link instead. */
const MAX_REMEMBERED_TOKENS = 20;
const LANDING = /^#\/invite\/([A-Za-z0-9_-]{16,64})$/;
const ORIGIN = /^https?:\/\/[^/?#\s]+$/;

/** The build's public origin, where the static /i/ landing page is deployed. */
function publicOrigin(): string | undefined {
  const value: unknown = import.meta.env["VITE_PUBLIC_APP_ORIGIN"];
  return typeof value === "string" && ORIGIN.test(value) ? value : undefined;
}

/** The link an invitee opens. Without a configured origin (druif's /proef/ build has no /i/ page)
 * the app's own route takes the token directly, still in the fragment. */
export function inviteUrl(token: string): string {
  const origin = publicOrigin();
  if (origin !== undefined) return `${origin}/i/#${token}`;
  return `${window.location.origin}${import.meta.env.BASE_URL}#/invite/${token}`;
}

/** The link a guest without an account opens to join a flight by its code. On the public origin it
 * goes through the same static /i/ page as an invite (so a chat shows the card, and the page hands
 * over to `#/join/<code>`); elsewhere it is the app's own route. */
export function joinInviteUrl(joinCode: string): string {
  const origin = publicOrigin();
  if (origin !== undefined) return `${origin}/i/#join/${joinCode}`;
  return `${window.location.origin}${import.meta.env.BASE_URL}#/join/${joinCode}`;
}

/** A route of the app as a full link: on the configured public origin when there is one, else on
 * this build's own address (druif serves the app under /proef/). */
export function appRouteUrl(route: string): string {
  const origin = publicOrigin();
  if (origin !== undefined) return `${origin}/#${route}`;
  return `${window.location.origin}${import.meta.env.BASE_URL}#${route}`;
}

const PendingInvite = z.object({ secret: InviteSecret, acceptAfterSignIn: z.boolean() });
export type PendingInvite = z.infer<typeof PendingInvite>;

export function stashPendingInvite(secret: InviteSecret, acceptAfterSignIn = false): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ secret, acceptAfterSignIn }));
  } catch {
    // Blocked storage: the landing still works in this page, only a reload loses the invite.
  }
}

export function readPendingInvite(): PendingInvite | undefined {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (raw === null) return undefined;
    const parsed = PendingInvite.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

export function clearPendingInvite(): void {
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing stored that could be read back.
  }
}

/** Takes the token of an `#/invite/<token>` address, stores it and rewrites the address to
 * `#/invite` without a history entry. Runs before the first render, so no screen, history entry or
 * analytics event ever holds the token. */
export function captureInviteFromLocation(): boolean {
  const token = LANDING.exec(window.location.hash)?.[1];
  if (token === undefined) return false;
  stashPendingInvite({ token });
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, "", `${pathname}${search}#/invite`);
  return true;
}

const RememberedTokens = z.array(z.tuple([z.string(), z.string()]));

function readTokens(): [string, string][] {
  try {
    const parsed = RememberedTokens.safeParse(
      JSON.parse(window.localStorage.getItem(TOKENS_KEY) ?? "[]"),
    );
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

/** The edge keeps only a link's hash, so a live link can be shared again only from the device that
 * made it. Newest last, bounded. */
export function rememberInviteToken(inviteId: string, token: string): void {
  const next = [
    ...readTokens().filter(([id]) => id !== inviteId),
    [inviteId, token] as [string, string],
  ];
  try {
    window.localStorage.setItem(TOKENS_KEY, JSON.stringify(next.slice(-MAX_REMEMBERED_TOKENS)));
  } catch {
    // Not remembered: the sheet makes a fresh link next time instead.
  }
}

export function knownInviteToken(inviteId: string): string | undefined {
  return readTokens().find(([id]) => id === inviteId)?.[1];
}

/** Signing out forgets them: the next person on this device must not re-share someone else's link. */
export function forgetInviteTokens(): void {
  try {
    window.localStorage.removeItem(TOKENS_KEY);
    window.localStorage.removeItem(TOKENS_OWNER_KEY);
  } catch {
    // Nothing stored that could be read back.
  }
}

/** Called on every session answer. No session, or an account other than the one the tokens were
 * kept for, forgets them, so a session that ends without a sign-out never hands them on. */
export function keepInviteTokensFor(userId: string | undefined): void {
  try {
    if (userId !== undefined && window.localStorage.getItem(TOKENS_OWNER_KEY) === userId) return;
    forgetInviteTokens();
    if (userId !== undefined) window.localStorage.setItem(TOKENS_OWNER_KEY, userId);
  } catch {
    // Blocked storage holds no tokens to hand on.
  }
}
